import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getStaticValues } from '../../store/actions/survey';
import Button from '../FormComponents/Button';
import Dropdown from '../FormComponents/Dropdown';
import TextField from '../FormComponents/TextField';
import ToastMessage from '../ToastMessage/ToastMessage';

import { EMAIL_REGEX } from '../../constants';
import { State } from '../../models/state';
import { getUser } from '../../store/actions/auth';
import { updateProfile } from '../../store/actions/profile';

function ProfileForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { languages } = useSelector((state: State) => state.survey);
  const { user } = useSelector((state: State) => state.auth);
  const [formData, setFormData] = useState({
    name: user.name,
    email: user.email,
    language: user.language,
    position: user.position,
  });
  const [formClicked, setFormClicked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [toast, setToast] = useState('');
  const [emailValid, setEmailValid] = useState(true);
  const requiredFields = ['name', 'email'];

  const getLanguagesAndRoles = () => {
    dispatch(getStaticValues());
  };

  const getData = () => {
    dispatch(getUser());
  };

  useEffect(() => {
    setFormData({
      name: user.name,
      email: user.email,
      language: user.language,
      position: user.position,
    });
  }, [user]);

  useEffect(() => {
    getLanguagesAndRoles();
    if (location?.state?.message) {
      setToastType('success');
      setToast(location?.state?.message);
      window.history.replaceState({}, '');
    }
    getData();
  }, []);

  const checkRequiredFields = () => {
    return requiredFields.every((key: string) => formData[key]);
  };

  const handleFormClick = () => {
    setFormClicked(true);
    if (!checkRequiredFields()) {
      setToastType('error');
      setToast('Please enter all mandatory fields');
      return;
    }
    if (!EMAIL_REGEX.test(formData.email)) {
      setEmailValid(false);
      setToastType('error');
      setToast('Please enter valid email');
      return;
    }
    setEmailValid(true);
    setLoading(true);
    dispatch(
      updateProfile(formData, (res, message) => {
        setLoading(false);
        if (res) {
          navigate('/', {
            state: {
              message: 'Profile is updated',
            },
          });
        } else {
          setToastType('error');
          setToast(message || 'Opps, unknown error occured.');
        }
      }),
    );
  };

  const handleLanguage = (lang: string) => {
    setFormData({ ...formData, language: lang });
  };

  return (
    <>
      <Box>
        <Typography variant='h5'>Profile</Typography>
        <List>
          {user.organization && (
            <ListItem>
              <Box display='flex' gap={1} alignItems='center'>
                <Typography fontWeight='bold'>Organization -</Typography>
                <Typography variant='body2'>{user.organization.name}</Typography>
              </Box>
            </ListItem>
          )}
          <ListItem>
            <Box display='flex' gap={1} alignItems='center'>
              <Typography fontWeight='bold'>Role -</Typography>
              <Typography variant='body2'>{user.role}</Typography>
            </Box>
          </ListItem>
          <ListItem>
            <Box display='flex' gap={1} alignItems='center'>
              <Typography fontWeight='bold'>Active -</Typography>
              <Typography variant='body2'>{user.active ? 'Yes' : 'No'}</Typography>
            </Box>
          </ListItem>
        </List>
        <Grid container marginTop={4} gap={4}>
          <Grid item xs={5}>
            <TextField
              label='Name'
              id='name'
              variant='outlined'
              style={{ width: '100%' }}
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              required={requiredFields.includes('name')}
              error={formClicked && requiredFields.includes('name') && !formData.name}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              label='Email'
              id='email'
              variant='outlined'
              style={{ width: '100%' }}
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              required={requiredFields.includes('email')}
              error={formClicked && requiredFields.includes('email') && (!formData.email || !emailValid)}
            />
          </Grid>
        </Grid>
        <Grid container marginTop={2} gap={4}>
          <Grid item xs={5}>
            <TextField
              label='Position'
              id='position'
              variant='outlined'
              style={{ width: '100%' }}
              value={formData.position}
              onChange={e => setFormData({ ...formData, position: e.target.value })}
            />
          </Grid>
          {user.role !== 'Admin' && (
            <Grid item xs={5}>
              <Dropdown
                value={formData.language}
                label='Language'
                style={{ width: '100%' }}
                options={languages}
                onChange={(_: SyntheticEvent, value: string) => handleLanguage(value)}
              />
            </Grid>
          )}
        </Grid>
        <Box className='submit-button'>
          <Button text='Update' onClick={handleFormClick} loading={loading} />
        </Box>
      </Box>
      <ToastMessage toast={toast} close={() => setToast('')} messageType={toastType} />
    </>
  );
}

export default ProfileForm;
